import {
    Button,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import {GridColDef, GridFilterItem, GridFilterOperator} from '@mui/x-data-grid'
import React, {SyntheticEvent, useEffect, useMemo, useState} from 'react'
import {DesktopDatePicker} from '@mui/x-date-pickers'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import SearchIcon from '@mui/icons-material/Search'
import {ValueOption} from '../../search/model/DisplayFiltersDTO'


interface CustomFilterInputComponentProps {
    colDef: GridColDef
    getPossibleValues: (col: GridColDef) => any[],
    filters: any[],
    setFilter: (filter: GridFilterItem) => void,
    operator: string
}

export interface CustomFilterOperator extends Omit<GridFilterOperator, 'InputComponent'> {
    InputComponent: React.JSXElementConstructor<CustomFilterInputComponentProps>
}

const DateRangeInputComponent = ({
                                     colDef,
                                     getPossibleValues,
                                     filters,
                                     setFilter,
                                     operator
                                 }: CustomFilterInputComponentProps) => {
    const [startDate, setStartDate] = useState<Dayjs | null>(null)
    const [endDate, setEndDate] = useState<Dayjs | null>(null)
    const [error, setError] = useState<string>('')

    const updateFilter = (start: Dayjs | null, end: Dayjs | null) => {
        setFilter({
            id: colDef.field,
            field: colDef.field,
            operator: operator,
            value: [start, end]
        })
    }

    const handleStartDateChange = (newStartDate: Dayjs | null) => {
        if (endDate && newStartDate && newStartDate > endDate) {
            setError('Start date cannot be after end date')
            return
        }
        setStartDate(newStartDate)
        updateFilter(newStartDate, endDate)
        setError('')
    }

    const handleEndDateChange = (newEndDate: Dayjs | null) => {
        if (startDate && newEndDate && newEndDate < startDate) {
            setError('End date cannot be before start date')
            return
        }
        setEndDate(newEndDate)
        updateFilter(startDate, newEndDate)
        setError('')
    }

    return (
        <div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className="date-range-picker">
                    <div className="date-picker-container">
                        <DesktopDatePicker
                            label="Start Date"
                            value={filters[0] ?? null}
                            onChange={handleStartDateChange}
                            slotProps={{
                                textField: {className: 'date-text-field', variant: 'outlined'}
                            }}
                            maxDate={dayjs()}
                        />
                        <div className="date-separator"></div>
                        <DesktopDatePicker
                            label="End Date"
                            value={filters[1] ?? null}
                            onChange={handleEndDateChange}
                            slotProps={{
                                textField: {className: 'date-text-field', variant: 'outlined'}
                            }}
                            maxDate={dayjs()}
                        />
                    </div>
                    {error && <Typography color="error">{error}</Typography>}
                </div>
            </LocalizationProvider>
        </div>
    )
}

const EqualsOperatorInput = ({
                                 colDef,
                                 getPossibleValues,
                                 filters,
                                 setFilter,
                                 operator
                             }: CustomFilterInputComponentProps) => {
    const [searchString, setSearchString] = useState<string>('')

    const existingValues = useMemo(
        () =>
            getPossibleValues(colDef).filter((value) => {
                return value.toString().toLowerCase().includes(searchString.toLowerCase())
            }),
        [colDef, getPossibleValues, searchString]
    )

    return (
        <div className="datx-list-filter">
            <TextField
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                variant="outlined"
                margin="normal"
                slotProps={{
                    inputLabel: {classes: {root: 'subFilterInput'}},
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon className="search-icon"/>
                            </InputAdornment>
                        )
                    }
                }}
                placeholder={colDef.headerName ?? colDef.field}
            />
            <div className="filter-checkboxes">
                {existingValues.map((fil) => {
                    return (
                        <FormControlLabel
                            key={fil}
                            control={
                                <Checkbox
                                    value={fil}
                                    checked={filters.includes(fil)}
                                    onChange={(e, checked) => {
                                        setFilter({
                                            id: colDef.field,
                                            field: colDef.field,
                                            operator: operator,
                                            value: checked
                                                ? [...filters, fil]
                                                : filters.filter((filter: string) => filter !== fil)
                                        })
                                    }}
                                />
                            }
                            label={fil}
                        />
                    )
                })}
            </div>
        </div>
    )
}

const getEqualsOperatorInputWithExternalValues = (externalValues?: ValueOption[]) => ({
                                                                                          colDef,
                                                                                          getPossibleValues,
                                                                                          filters,
                                                                                          setFilter,
                                                                                          operator
                                                                                      }: CustomFilterInputComponentProps) => {
    const [searchString, setSearchString] = useState<string>('')
    const [selectedValues, setSelectedValues] = useState(filters)
    const [isApplyingFilter, setIsApplyingFilter] = useState(false)

    const values: ValueOption[] = useMemo(() => {
        return externalValues ?? getPossibleValues(colDef).map(value => {
            return {value: value, targetId: value}
        })
    }, [externalValues, getPossibleValues(colDef)])

    const existingValues = useMemo(
        () =>
            values.filter((value) => {
                return value.value.toString().toLowerCase().includes(searchString.toLowerCase())
            }),
        [values, searchString]
    )
    const isSelectAll = useMemo(() => {
        return existingValues.map(value => value.targetId).every(value => selectedValues.includes(value))
    }, [existingValues, searchString, selectedValues])

    const handleScroll = (event: SyntheticEvent) => event.stopPropagation()

    const applyFilter = (event: SyntheticEvent) => {
        setIsApplyingFilter(true)
        setFilter({
            id: colDef.field,
            field: colDef.field,
            operator: operator,
            value: selectedValues
        })
    }

    const isFilterUpdated = useMemo(() => JSON.stringify(selectedValues.sort()) !== JSON.stringify(filters.sort()), [selectedValues, filters])
    const isEmptyFilter = filters.length === 0

    const resetFilters = (event: SyntheticEvent) => {
        if (!isEmptyFilter) {
            setFilter({
                id: colDef.field,
                field: colDef.field,
                operator: operator,
                value: null
            })
        }
    }

    return (
        <div className="datx-list-filter">
            <TextField
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                variant="outlined"
                margin="normal"
                slotProps={{
                    inputLabel: {classes: {root: 'subFilterInput'}},
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon className="search-icon"/>
                            </InputAdornment>
                        )
                    }
                }}
                placeholder={colDef.headerName ?? colDef.field}
            />
            <div className="filter-checkboxes" onScroll={handleScroll} onWheel={handleScroll}>
                <FormControlLabel
                    key="All"
                    control={
                        <Checkbox
                            value="All"
                            checked={isSelectAll}
                            onChange={(e, checked) => {
                                setSelectedValues(checked ? existingValues.map(value => value.targetId) : [])
                            }}
                        />
                    }
                    label="(Select All)"
                />
                {existingValues.map((value) => {
                    return (
                        <FormControlLabel
                            key={value.targetId}
                            control={
                                <Checkbox
                                    value={value.targetId}
                                    checked={selectedValues.includes(value.targetId)}
                                    onChange={(e, checked) => {
                                        setSelectedValues(
                                            checked
                                                ? [...selectedValues, value.targetId]
                                                : selectedValues.filter((selectedValue: string) => selectedValue !== value.targetId)
                                        )
                                    }}
                                />
                            }
                            label={value.value}
                        />
                    )
                })}
            </div>
            <div className="filter-action-panel">
                <Button
                    variant="outlined"
                    color="primary"
                    data-testid="reset-search-filters"
                    onClick={resetFilters}
                    disabled={isEmptyFilter || isApplyingFilter}
                >
                    Reset
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    data-testid="apply-search-filters"
                    onClick={applyFilter}
                    disabled={!isFilterUpdated || isApplyingFilter}
                >
                    Apply
                </Button>
            </div>
        </div>
    )
}

const SelectOperatorInput = ({
                                 colDef,
                                 getPossibleValues,
                                 filters,
                                 setFilter,
                                 operator
                             }: CustomFilterInputComponentProps) => {
    const existingValues = useMemo(() => getPossibleValues(colDef), [colDef, getPossibleValues])

    return (
        <Select
            onChange={(e) => {
                setFilter({
                    id: colDef.field,
                    field: colDef.field,
                    operator: operator,
                    value: [e.target.value]
                })
            }}
            value={filters.length ? filters[0] : ''}
            displayEmpty
        >
            <MenuItem value="">No Filter</MenuItem>
            {existingValues.map((value) => {
                return (
                    <MenuItem key={value} value={value}>
                        {value}
                    </MenuItem>
                )
            })}
        </Select>
    )
}

const MatchStringOperatorInput = ({
                                      colDef,
                                      getPossibleValues,
                                      filters,
                                      setFilter,
                                      operator
                                  }: CustomFilterInputComponentProps) => {
    return (
        <TextField
            value={filters[0] || ''}
            onChange={(e) =>
                setFilter({
                    id: colDef.field,
                    field: colDef.field,
                    operator: operator,
                    value: [e.target.value]
                })
            }
            variant="outlined"
            margin="normal"
            slotProps={{
                inputLabel: {classes: {root: 'subFilterInput'}},
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            {<SearchIcon className="search-icon"/>}
                        </InputAdornment>
                    )
                }
            }}
            placeholder={colDef.headerName ?? colDef.field}
        />
    )
}

const MatchStringOperatorInputWithMinimumCharacters = ({
                                                           colDef,
                                                           getPossibleValues,
                                                           filters,
                                                           setFilter,
                                                           operator
                                                       }: CustomFilterInputComponentProps) => {

    const [matchString, setMatchString] = useState('')
    const [focused, setFocused] = useState(false)

    const isValidSearchString = matchString.trim().length >= 3

    useEffect(() => {
        setMatchString(filters[0] || '')
    }, [filters])

    const handleSearchValue = (e: any) => {
        const value = e.target.value
        setMatchString(value)
    }

    const search = () => {
        const filterValue = isValidSearchString ? [matchString] : null
        setFilter({
            id: colDef.field,
            field: colDef.field,
            operator: operator,
            value: filterValue
        })
    }

    return (
        <Tooltip
            title="Please type at least 3 characters to perform a search"
            open={focused && !isValidSearchString}
            arrow
        >
            <TextField
                value={matchString}
                onChange={e => handleSearchValue(e)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                variant="outlined"
                margin="normal"
                slotProps={{
                    inputLabel: {classes: {root: 'subFilterInput'}},
                    input: {
                        endAdornment: (
                            <InputAdornment position="end" onClick={search}>
                                {<SearchIcon className="search-icon"/>}
                            </InputAdornment>
                        ), onKeyUp: (event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                search()
                            }
                        }
                    }
                }}
                placeholder={colDef.headerName ?? colDef.field}
            />
        </Tooltip>
    )
}

export const equalsOperator: CustomFilterOperator = {
    label: 'equals',
    value: 'equals',
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator || !filterItem.value.length) {
            return null
        }

        return (value, row, colDef, apiRef) => {
            return filterItem.value.some((filterValue: string) => String(value) === filterValue)
        }
    },
    InputComponent: EqualsOperatorInput
}


export const equalsOperatorWithExternalValues = (values?: ValueOption[]): CustomFilterOperator => {
    return {
        label: 'equals',
        value: 'equals',
        getApplyFilterFn: (filterItem) => {
            if (
                !filterItem.field ||
                !filterItem.value ||
                !filterItem.operator ||
                !filterItem.value.length ||
                !filterItem.value[0]
            ) {
                return null
            }
            return (value, row, colDef, apiRef) => {
                return filterItem.value === String(value)
            }
        },
        InputComponent: getEqualsOperatorInputWithExternalValues(values)
    }
}

export const comboBoxSelect: CustomFilterOperator = {
    label: 'comboSelect',
    value: 'comboSelect',
    getApplyFilterFn: (filterItem) => {
        if (
            !filterItem.field ||
            !filterItem.value ||
            !filterItem.operator ||
            !filterItem.value.length ||
            !filterItem.value[0]
        ) {
            return null
        }
        return (value, row, colDef, apiRef) => {
            return filterItem.value === String(value)
        }
    },
    InputComponent: SelectOperatorInput
}

export const matchString: CustomFilterOperator = {
    label: 'matchString',
    value: 'matchString',
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator || !filterItem.value.length) {
            return null
        }

        return (value, row, colDef, apiRef) => {
            return String(value).toLowerCase().includes(filterItem.value[0].toLowerCase())
        }
    },
    InputComponent: MatchStringOperatorInput
}

export const matchStringOperatorInputWithMinimumCharacters: CustomFilterOperator = {
    label: 'matchString',
    value: 'matchString',
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator || !filterItem.value.length) {
            return null
        }

        return (value, row, colDef, apiRef) => {
            return String(value).toLowerCase().includes(filterItem.value[0].toLowerCase())
        }
    },
    InputComponent: MatchStringOperatorInputWithMinimumCharacters
}

export const dateRangeMatch: CustomFilterOperator = {
    label: 'dateRangeMatch',
    value: 'dateRangeMatch',
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator || !filterItem.value.length) {
            return null
        }

        const [startDate, endDate] = filterItem.value

        return (value, row, colDef, apiRef) => {
            if (!value || !Array.isArray(value)) return false
            const [year, month, day, hour, minute, second, millisecond] = value
            const fixedMillisecond = millisecond % 1000
            const transactionDate = new Date(year, month - 1, day, hour, minute, second, fixedMillisecond)

            const start = startDate ? new Date(startDate) : null
            const end = endDate ? new Date(endDate) : null

            return (!start || transactionDate >= start) && (!end || transactionDate <= end)
        }
    },
    InputComponent: DateRangeInputComponent
}

export const operatorMap: { [key: string]: CustomFilterOperator } = {
    equals: equalsOperator,
    comboSelect: comboBoxSelect,
    matchString: matchString,
    matchStringWithTooltip: matchStringOperatorInputWithMinimumCharacters,
    dateRangeMatch: dateRangeMatch
}
